import {
  Container,
  Stack,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Usb as UsbIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { useDashboard } from '@/hooks/useDashboard';
import { useDeviceStore } from '@/store/useDeviceStore';
import {
  SummaryCards,
  TestsTimelineChart,
  RecentTestsTable,
  DashboardLoadingSkeleton,
  DashboardEmptyState,
  ErrorState,
} from './components';

export function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useDashboard();
  const deviceInfo = useDeviceStore((s) => s.deviceInfo);
  const isDeviceConnected = !!deviceInfo?.uid;

  // Loading state
  if (isLoading) {
    return <DashboardLoadingSkeleton />;
  }

  // Error state
  if (error || !data) {
    return (
      <ErrorState
        message="Failed to load dashboard data"
        onRetry={refetch}
      />
    );
  }

  // Empty state for new users
  if (data.totalTests === 0) {
    return <DashboardEmptyState />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack spacing={4}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome back! Here's an overview of your testing activity.
            </Typography>
          </Box>


        </Box>

        {/* Device Status Banner (if connected) */}
        {isDeviceConnected && (
          <Alert severity="info" icon={<UsbIcon />}>
            Device {deviceInfo.uid} is connected and ready.
          </Alert>
        )}

        {/* Summary Cards */}
        <SummaryCards data={data} />

        {/* Tests Timeline Chart */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tests Timeline (Last 30 Days)
            </Typography>
            <TestsTimelineChart data={data.testsByDay} />
          </CardContent>
        </Card>

        {/* Recent Tests */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Recent Tests</Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/tests')}
              >
                View All
              </Button>
            </Box>
            <RecentTestsTable tests={data.recentTests} />
          </CardContent>
        </Card>

        {/* Quick Demo Actions (Development) */}
        {import.meta.env.DEV && (
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Development Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/measurement/results/current?from=/')}
                >
                  Demo: Progress/Results View
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/measurement/new')}
                >
                  Demo: Measurement Wizard
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/measurement/new?mock=true')}
                >
                  Demo: Wizard Simulation
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
